import React, { useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  AllCommunityModule,
  ModuleRegistry,
  ICellRendererParams,
  IGetRowsParams,
  RowStyle,
} from 'ag-grid-community';
import AddOpponentModal from '../../components/modals/AddOpponentModal';
import ConfirmDialog from '../../components/modals/ConfirmDialog';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { PermissionGuard } from '../../components/common/PermissionGuard';
import 'react-toastify/dist/ReactToastify.css';
// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface OpponentData {
  _id?: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  clientId?: string;
  clientName?: string;
  [key: string]: any;
}

const Opponents: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [opponentToDelete, setOpponentToDelete] = useState<OpponentData | null>(null);
  const [editOpponent, setEditOpponent] = useState<OpponentData | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const clientIdFromSession = sessionManager.getClientId();
  const gridApi = useRef<any>(null);

  // Define the datasource outside of onGridReady
  const datasource = useMemo(
    () => ({
      getRows: async (getRowsParams: IGetRowsParams) => {
        const page = Math.floor(getRowsParams.startRow / pageSize);
        const limit = getRowsParams.endRow - getRowsParams.startRow;
        if (getRowsParams.startRow === 0) setIsLoading(true);

        try {
          const params: any = {
            page: page + 1,
            limit: limit,
          };
          // Scope to current client when selected in session
          if (clientIdFromSession) {
            params.clientId = clientIdFromSession;
          }

          const response = await api.get(API_URLS.GET_OPPONENTS, { params });
          if (response.data.success) {
            getRowsParams.successCallback(response.data.data, response.data.total);
            if (getRowsParams.startRow === 0) setIsLoading(false);
          } else {
            showErrorMsg(response.data.message || 'Failed to fetch opponents');
            getRowsParams.failCallback();
            setIsLoading(false);
          }
        } catch (error) {
          showErrorMsg('Failed to fetch opponents.');
          getRowsParams.failCallback();
          setIsLoading(false);
        }
      },
    }),
    [pageSize, clientIdFromSession],
  );

  // On grid ready, just save the api ref
  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    setIsLoading(true);
  };

  // Handle pagination changes (including page size changes)
  const onPaginationChanged = (params?: any) => {
    if (!gridApi.current) return;
    const newPageSize = gridApi.current.paginationGetPageSize();
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Edit button logic
  const handleEditClick = (opponent: OpponentData) => {
    setEditOpponent(opponent);
    setIsModalOpen(true);
  };

  // Delete button logic
  const handleDeleteClick = (opponent: OpponentData) => {
    setOpponentToDelete(opponent);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (opponentToDelete && opponentToDelete._id) {
      try {
        const response = await api.delete(API_URLS.DELETE_OPPONENT, {
          params: { _id: opponentToDelete._id },
        });
        if (response.data.success) {
          showSuccessMsg('Opponent deleted successfully!');
          if (gridApi.current) {
            gridApi.current.purgeInfiniteCache();
          }
        } else {
          showErrorMsg(response.data.message || 'Failed to delete opponent');
        }
      } catch (error) {
        showErrorMsg('Failed to delete opponent.');
      }
    }
    setIsDeleteDialogOpen(false);
    setOpponentToDelete(null);
  };

  // Only refresh grid after add or update
  const handleOpponentAddedOrUpdated = () => {
    setIsModalOpen(false);
    setEditOpponent(null);
    if (gridApi.current) {
      gridApi.current.purgeInfiniteCache();
    }
  };

  // Only close modal, do not refresh grid
  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditOpponent(null);
  };

  // No client selection dropdown; grid is scoped by session client

  // Custom cell renderer for actions column
  const ActionsCellRenderer: React.FC<ICellRendererParams<OpponentData>> = ({ data }) => {
    if (!data) return null;

    const canEdit = sessionManager.canPerformAction('opponents', 'edit');
    const canDelete = sessionManager.canPerformAction('opponents', 'delete');

    return (
      <div className="flex items-center justify-center space-x-2">
        {canEdit && (
          <button
            onClick={() => handleEditClick(data)}
            className="text-blue-600 hover:text-blue-800 transform hover:scale-110 transition-all"
            title="Edit Opponent"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
              />
            </svg>
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => handleDeleteClick(data)}
            className="text-red-600 hover:text-red-800 transform hover:scale-110 transition-all"
            title="Delete Opponent"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const columnDefs: ColDef<OpponentData>[] = [
    {
      headerName: 'No',
      sortable: false,
      flex: 1,
      minWidth: 80,
      valueGetter: (params) => (params.node?.rowIndex ? params.node.rowIndex + 1 : 1),
    },
    {
      headerName: 'Name',
      sortable: true,
      flex: 2,
      minWidth: 150,
      field: 'name',
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      sortable: false,
      width: 100,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 100,
      },
    },
  ];

  // Function to apply alternating row styles
  const getRowStyle = (params: any): RowStyle => {
    if (params.node.rowIndex % 2 === 1) {
      return { backgroundColor: 'rgb(var(--sd-mint))' };
    }
    return {};
  };

  return (
    <div className="w-full p-4">
      <div className="flex justify-between items-center gap-2 mb-4">
        <div className="flex items-center space-x-4">
          <h1 className="text-xl xss:text-2xl font-bold">Opponents</h1>
        </div>
        <PermissionGuard resource="opponents" action="add">
          <button
            onClick={() => {
              setEditOpponent(null);
              setIsModalOpen(true);
            }}
            className="text-sm xss:text-base bg-sd-green text-black font-semibold px-2 xss:px-4 py-1 rounded hover:bg-sd-green transition-colors shadow-2xl"
          >
            Add Opponent
          </button>
        </PermissionGuard>
      </div>

      <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowModelType="infinite"
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100]}
          cacheBlockSize={pageSize}
          datasource={datasource}
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          loading={isLoading}
          blockLoadDebounceMillis={50}
          maxBlocksInCache={2}
          infiniteInitialRowCount={pageSize}
          overlayLoadingTemplate={
            '<div role="status"><svg aria-hidden="true" class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg><span class="sr-only">Loading...</span></div>'
          }
          overlayNoRowsTemplate={'<span class="ag-overlay-no-rows-center">No opponents found</span>'}
          getRowStyle={getRowStyle}
        />
      </div>

      {isModalOpen && (
        <AddOpponentModal
          onClose={handleModalClose}
          onOpponentAdded={handleOpponentAddedOrUpdated}
          editOpponent={editOpponent}
        />
      )}
      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Opponent"
          message={`Are you sure you want to delete ${opponentToDelete?.name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
    </div>
  );
};

export default Opponents;
